import { Box, Button, Input, Text } from "@saleor/macaw-ui-next";
import { useIntl } from "react-intl";

import { portalMessages as m } from "./messages";
import { weekdayShort } from "./utils";

export type Week = Record<number, Array<{ start: string; end: string }>>;

// Mon-Fri, 8 h a day = 40 h a week.
const template = (afternoonStart: string, afternoonEnd: string): Week =>
  Object.fromEntries(
    [0, 1, 2, 3, 4].map(day => [
      day,
      [
        { start: "09:00", end: "13:00" },
        { start: afternoonStart, end: afternoonEnd },
      ],
    ]),
  );

export const WEEK_LUNCH_1H = template("14:00", "18:00");
export const WEEK_LUNCH_90 = template("14:30", "18:30");

export const weekFromRows = (rows: Array<{ weekday: number; start: string; end: string }>) => {
  const week: Week = {};

  rows.forEach(row => {
    week[row.weekday] = [...(week[row.weekday] ?? []), { start: row.start, end: row.end }];
  });

  return week;
};

export const weekToRows = (week: Week) =>
  Object.entries(week).flatMap(([weekday, rows]) =>
    rows
      .filter(row => row.start && row.end)
      .map(row => ({ weekday: Number(weekday), start: row.start, end: row.end })),
  );

const minutes = (hhmm: string) => {
  const [h, mm] = hhmm.split(":").map(Number);

  return h * 60 + mm;
};

export const weekHours = (week: Week) =>
  Object.values(week)
    .flat()
    .reduce((sum, row) => sum + Math.max(0, minutes(row.end) - minutes(row.start)), 0) / 60;

/** Read-only summary: "seg. 09:00–13:00, 14:30–18:30 · …" and weekly hours. */
export const WeekSummary = ({ week }: { week: Week }) => {
  const intl = useIntl();

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {[0, 1, 2, 3, 4, 5, 6]
        .filter(day => (week[day] ?? []).length)
        .map(day => (
          <Text key={day} size={2}>
            {weekdayShort(intl.locale, day)}{" "}
            {(week[day] ?? []).map(row => `${row.start}–${row.end}`).join(", ")}
          </Text>
        ))}
      <Text size={2} color="default2">
        {intl.formatMessage(m.hoursPerWeekTotal, { hours: weekHours(week) })}
      </Text>
    </Box>
  );
};

/** Weekly hours editor with one-click 40 h templates. */
export const WeekScheduleEditor = ({
  week,
  onChange,
}: {
  week: Week;
  onChange: (week: Week) => void;
}) => {
  const intl = useIntl();

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
        <Button size="small" variant="secondary" onClick={() => onChange(WEEK_LUNCH_90)}>
          {intl.formatMessage(m.templateLunch90)}
        </Button>
        <Button size="small" variant="secondary" onClick={() => onChange(WEEK_LUNCH_1H)}>
          {intl.formatMessage(m.templateLunch60)}
        </Button>
        <Text size={2} color="default2">
          {intl.formatMessage(m.hoursPerWeekTotal, { hours: weekHours(week) })}
        </Text>
      </Box>
      {[0, 1, 2, 3, 4, 5, 6].map(weekday => (
        <Box key={weekday} display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <Text size={2} __width="40px">
            {weekdayShort(intl.locale, weekday)}
          </Text>
          {(week[weekday] ?? []).map((row, index) => (
            <Box key={index} display="flex" gap={1} alignItems="center">
              <Input
                size="small"
                type="time"
                value={row.start}
                onChange={event => {
                  const rows = [...(week[weekday] ?? [])];

                  rows[index] = { ...rows[index], start: event.target.value };
                  onChange({ ...week, [weekday]: rows });
                }}
              />
              –
              <Input
                size="small"
                type="time"
                value={row.end}
                onChange={event => {
                  const rows = [...(week[weekday] ?? [])];

                  rows[index] = { ...rows[index], end: event.target.value };
                  onChange({ ...week, [weekday]: rows });
                }}
              />
              <Button
                size="small"
                variant="tertiary"
                onClick={() =>
                  onChange({
                    ...week,
                    [weekday]: (week[weekday] ?? []).filter((_, i) => i !== index),
                  })
                }
              >
                ×
              </Button>
            </Box>
          ))}
          <Button
            size="small"
            variant="secondary"
            onClick={() =>
              onChange({
                ...week,
                [weekday]: [...(week[weekday] ?? []), { start: "09:00", end: "13:00" }],
              })
            }
          >
            +
          </Button>
        </Box>
      ))}
    </Box>
  );
};
